/**
 * useAddPackToCart -- React hook for adding a pack/bundle to the cart.
 *
 * Creates a cart first if none exists (PackPage is usually entered directly,
 * without any prior product-add flow). Then POSTs /v1/storefront/carts/:token/packs via BFF.
 */

'use client';

import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cartService } from '@/infrastructure/config/services.config';
import { useCartContext } from '@/lib/context/cart-context';
import { useIntl } from '@/lib/intl';
import { storefrontLayoutLogger } from '@/lib/logger';
import { PackSelection } from '@/components/organisms/BundleBuilder/BundleBuilder';

export interface AddPackToCartInput {
  packId: string;
  selections: PackSelection[];
}

export function useAddPackToCart() {
  const queryClient = useQueryClient();
  const { cartToken, setCartToken, showCart } = useCartContext();
  const { countryConfig } = useIntl();
  const creatingCartRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (input: AddPackToCartInput) => {
      let token = cartToken;
      if (!token) {
        if (creatingCartRef.current) {
          await new Promise<void>((resolve) => setTimeout(resolve, 500));
          token = cartToken;
        }
        if (!token) {
          creatingCartRef.current = true;
          try {
            const newCart = await cartService.createCart({
              countryCode: countryConfig.countryCode,
              currency: countryConfig.currency.code,
              vendor: 'shapermint',
              shop: 'shapermint',
            });
            token = newCart.token;
            setCartToken(token);
          } finally {
            creatingCartRef.current = false;
          }
        }
      }

      if (!token) {
        throw new Error('Cart token missing after createCart');
      }

      const result = await cartService.addPackToCart(token, input.packId, input.selections);
      return { cartToken: result.cartToken };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['cart', result.cartToken] });
      showCart();
    },
    onError: (error) => {
      storefrontLayoutLogger.error(
        '[useAddPackToCart] Failed to add pack',
        error instanceof Error ? error : undefined,
      );
    },
  });

  const addPack = useCallback(
    async (packId: string, selections: PackSelection[]) => {
      if (mutation.isPending) return;
      mutation.reset();
      await mutation.mutateAsync({ packId, selections });
    },
    [mutation],
  );

  return {
    addPack,
    isAdding: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    reset: mutation.reset,
  };
}
